import { Fragment, ReactNode, useCallback, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import AppGlobalsContext from "app/AppGlobalsContext";
import { getBrandDisplayName } from "app/reducers/partnerSelector";
import { formatAmountForDisplay, getCurrencySymbol } from "app/utils/utils";
import { Amount } from "app/components/ui/Amount/Amount";

/**
 * Template tokens use the syntax `{type}` or `{type:value}`.
 *
 * Default tokens (built from Redux + `AppGlobalsContext`, see below):
 * - `{brandName}`      → partner display name, e.g. "Air France"
 * - `{brand}`          → brand code, e.g. "AF"
 * - `{marketingCode}`  → partnerCode, e.g. "AFFR"
 * - `{shop}`           → shop, e.g. "fr-FR"
 * - `{langue}`         → language part of the shop (`shop.slice(0, 2)`), e.g. "fr"
 * - `{currency}`       → the current shop's currency symbol, e.g. "€"
 * - `{currency:1234}`  → the value formatted as a price in the current shop currency
 *
 * A token whose type has no handler — or whose handler returns null/undefined — is left as-is.
 * Both hooks below accept an `extraHandlers` map to add or override tokens per call site.
 */
const TEMPLATE_TOKEN_SPLIT_PATTERN = /(\{[^{}]+\})/g;
const TEMPLATE_TOKEN_PATTERN = /^\{([^{}:]+)(?::([^{}]+))?\}$/;
const TEMPLATE_TOKEN_REPLACE_PATTERN = /\{([^{}:]+)(?::([^{}]+))?\}/g;

type ElementTemplateHandlers = Record<string, (value?: string) => ReactNode>;
type StringTemplateHandlers = Record<string, (value?: string) => string | null | undefined>;

export type DefaultTemplateValues = {
	brandName: string;
	brand: string;
	marketingCode: string;
	langue: string;
	shop: string;
};

/**
 * Resolves the argument-less default tokens (`brandName`, `brand`, `marketingCode`, `langue`,
 * `shop`) to their plain values, for call sites that need an already-resolved `values` map
 * (e.g. `CustomFormattedMessage`'s `values` prop) rather than a handler function per token.
 * `currency` is not included here — it needs a value per token instance (`{currency:1234}`),
 * so use `useDefaultTemplateHandlers` for it instead.
 */
export const useDefaultTemplateValues = (): DefaultTemplateValues => {
	const { shop = "", brand, partnerCode } = useContext(AppGlobalsContext);
	const brandDisplayName = useSelector(getBrandDisplayName);

	return useMemo(
		() => ({
			brandName: brandDisplayName,
			brand,
			marketingCode: partnerCode,
			langue: shop.slice(0, 2),
			shop,
		}),
		[brandDisplayName, brand, partnerCode, shop]
	);
};

/**
 * Builds the default token handlers (`brandName`, `brand`, `marketingCode`, `langue`, `shop`,
 * `currency`) in both flavors: `elementHandlers` return React nodes (used by
 * `useTemplateRender`), `stringHandlers` return strings (used by `useTemplateRenderToString`
 * and by call sites — e.g. `CustomFormattedMessageContainer` — that need a callable `currency`
 * entry to plug into another component's own `values` map).
 */
export const useDefaultTemplateHandlers = () => {
	const { shop = "", brand, partnerCode } = useContext(AppGlobalsContext);
	const brandDisplayName = useSelector(getBrandDisplayName);
	const currencyCode = useSelector((state: { currency: string }) => state.currency);
	const intl = useIntl();

	const textHandlers = useMemo(
		() => ({
			brandName: () => brandDisplayName,
			brand: () => brand,
			marketingCode: () => partnerCode,
			langue: () => shop.slice(0, 2),
			shop: () => shop,
		}),
		[brandDisplayName, brand, partnerCode, shop]
	);

	const elementHandlers = useMemo<ElementTemplateHandlers>(
		() => ({
			...textHandlers,
			currency: value => {
				if (value === undefined) {
					return getCurrencySymbol(shop, currencyCode);
				}
				return <Amount value={Number(value)} data-testid="amount" />;
			},
		}),
		[textHandlers, shop, currencyCode]
	);

	const stringHandlers = useMemo<StringTemplateHandlers>(
		() => ({
			...textHandlers,
			currency: value => {
				if (value === undefined) {
					return getCurrencySymbol(shop, currencyCode);
				}

				const amount = Number(value);

				if (isNaN(amount)) {
					return null;
				}

				const money = intl.formatNumber(amount, {
					style: "currency",
					currency: currencyCode,
					currencyDisplay: "symbol",
					useGrouping: true,
					minimumFractionDigits: 0,
					maximumFractionDigits: 2,
				});

				return formatAmountForDisplay(money, currencyCode, shop);
			},
		}),
		[textHandlers, intl, currencyCode, shop]
	);

	return { elementHandlers, stringHandlers };
};

/**
 * Renders a template string into an array of strings and React elements: plain text is kept
 * as-is, each known token is replaced by its handler's result (which may be a component,
 * e.g. `{currency:1234}` → <Amount />).
 */
export const useTemplateRender = () => {
	const { elementHandlers } = useDefaultTemplateHandlers();

	return useCallback(
		(paragraph: unknown, extraHandlers: ElementTemplateHandlers = {}): ReactNode => {
			if (typeof paragraph !== "string") {
				return paragraph as ReactNode;
			}

			const handlers = { ...elementHandlers, ...extraHandlers };
			const tokens = paragraph.split(TEMPLATE_TOKEN_SPLIT_PATTERN);

			return tokens.map((token, tokenIndex) => {
				const match = token.match(TEMPLATE_TOKEN_PATTERN);

				if (!match) {
					return token;
				}

				const [, type, value] = match;
				const handler = handlers[type];

				if (!handler) {
					return token;
				}

				return <Fragment key={tokenIndex}>{handler(value)}</Fragment>;
			});
		},
		[elementHandlers]
	);
};

/**
 * String-only variant of `useTemplateRender`, for `dangerouslySetInnerHTML` use cases where
 * React elements cannot be injected: every handler result is coerced to a string.
 * Run the result through `htmlSanitize` before injecting it, since injected values are not
 * sanitized here.
 */
export const useTemplateRenderToString = () => {
	const { stringHandlers } = useDefaultTemplateHandlers();

	return useCallback(
		(paragraph: unknown, extraHandlers: StringTemplateHandlers = {}): unknown => {
			if (typeof paragraph !== "string") {
				return paragraph;
			}

			const handlers = { ...stringHandlers, ...extraHandlers };

			return paragraph.replace(TEMPLATE_TOKEN_REPLACE_PATTERN, (token, type, value) => {
				const handler = handlers[type];

				if (!handler) {
					return token;
				}

				const replacement = handler(value);

				if (replacement === null || replacement === undefined) {
					return token;
				}

				return String(replacement);
			});
		},
		[stringHandlers]
	);
};
